import {useState} from "react";
import {Button, Menu, MenuItem, Box} from "@mui/material";
import {KeyboardArrowDown as ArrowDownIcon} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {colors, shadows, borders} from "./colors";

/**
 * Reusable actions dropdown button.
 *
 * Props:
 *  - label: string — button label
 *  - actions: Array<{ label, icon?, onClick, color?, danger? }>
 *  - disabled?: boolean
 *  - count?: number — show count in label (e.g. "פעולות (3)")
 *  - size?: "small" | "medium"
 *  - sx?: object
 */
const ActionMenu = ({
                      label = "פעולות",
                      actions = [],
                      disabled = false,
                      count,
                      size = "small",
                      sx,
                    }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const displayLabel = count !== undefined ? `${label} (${count})` : label;

  return (
      <RTL>
        <Button
            disabled={disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            endIcon={<ArrowDownIcon/>}
            variant="contained"
            size={size}
            sx={{
              backgroundColor: disabled ? colors.borderHover : colors.dark,
              color: colors.white,
              fontSize: "13px",
              fontWeight: 600,
              padding: size === "small" ? "6px 16px" : "8px 20px",
              borderRadius: borders.radius.md,
              textTransform: "none",
              "&:hover": {
                backgroundColor: disabled ? colors.borderHover : colors.primaryDark,
              },
              "&:disabled": {
                backgroundColor: colors.borderHover,
                color: colors.textMuted,
              },
              ...sx,
            }}
        >
          {displayLabel}
        </Button>

        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: borders.radius.lg,
                border: borders.default,
                boxShadow: shadows.dropdown,
                padding: "4px",
                minWidth: "180px",
              }
            }}
        >
          {actions.map((action, idx) => (
              <MenuItem
                  key={idx}
                  onClick={() => {
                    action.onClick();
                    setAnchorEl(null);
                  }}
                  sx={{
                    gap: 1.5,
                    fontSize: "14px",
                    fontWeight: 500,
                    padding: "10px 14px",
                    borderRadius: borders.radius.sm,
                    color: action.danger ? colors.error : colors.textSecondary,
                    "&:hover": {
                      backgroundColor: action.danger ? colors.errorBg : colors.primarySubtle,
                      color: action.danger ? colors.error : colors.primary,
                    },
                  }}
              >
                {action.icon && (
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      color: action.danger ? colors.error : colors.textSecondary,
                    }}>
                      {action.icon}
                    </Box>
                )}
                {action.label}
              </MenuItem>
          ))}
        </Menu>
      </RTL>
  );
};

export default ActionMenu;
